import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type Language = 'pt' | 'en';

type LocalizedText = Record<Language, string>;

type TravelIdea = {
  key: string;
  image: string;
  label: LocalizedText;
  title: LocalizedText;
  description: LocalizedText;
  signals: Record<Language, string[]>;
};

const getLanguage = (language?: string): Language => (
  language?.toLowerCase().startsWith('pt') ? 'pt' : 'en'
);

const sectionCopy = {
  badge: {
    pt: 'Ideias curadas',
    en: 'Curated ideas',
  },
  title: {
    pt: 'Ideias de viagem curadas pela Aero Destiny',
    en: 'Travel ideas curated by Aero Destiny',
  },
  description: {
    pt:
      'Explore pontos de partida para viagens com propósito. Nossa equipe refina rotas, hotéis, logística e experiências de acordo com o seu perfil.',
    en:
      'Explore starting points for meaningful trips. Our team refines routes, hotels, logistics and experiences around your profile.',
  },
  investment: {
    pt: 'Investimento definido após revisão',
    en: 'Investment defined after consultant review',
  },
  catalogNote: {
    pt: 'Catálogo editorial para inspirar a conversa com a consultoria.',
    en: 'Editorial catalog for shaping the conversation with our travel team.',
  },
  designLabel: {
    pt: 'Ideia para Travel Design',
    en: 'Travel Design idea',
  },
  processTitle: {
    pt: 'Como uma ideia vira uma viagem real',
    en: 'How an idea becomes a real trip',
  },
  process: [
    {
      title: {
        pt: '1. Direção inicial',
        en: '1. Initial direction',
      },
      description: {
        pt: 'Você escolhe uma ideia ou cria um pedido do zero para orientar o desenho da viagem.',
        en: 'You choose an idea or start from scratch to guide the travel design.',
      },
    },
    {
      title: {
        pt: '2. Revisão da consultoria',
        en: '2. Consultant review',
      },
      description: {
        pt: 'A Aero Destiny ajusta rota, hotéis, logística e ritmo antes de transformar a ideia em proposta.',
        en: 'Aero Destiny reviews route, hotels, logistics and pacing before turning the idea into a proposal.',
      },
    },
    {
      title: {
        pt: '3. Roteiro executável',
        en: '3. Executable itinerary',
      },
      description: {
        pt: 'O resultado final prioriza clareza, suporte humano e uma viagem possível de executar.',
        en: 'The final result prioritizes clarity, human support and a trip that can actually be executed.',
      },
    },
  ],
};

const travelIdeas: TravelIdea[] = [
  {
    key: 'kenya-conservation',
    image:
      'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=900&q=80',
    label: {
      pt: 'Natureza e cultura',
      en: 'Nature & culture',
    },
    title: {
      pt: 'Safari no Quênia com foco em conservação',
      en: 'Kenya Safari & Conservation Journey',
    },
    description: {
      pt: 'Vida selvagem, direção de lodges e guias locais com ritmo pensado para reservas e conservação.',
      en: 'Wildlife, lodge direction and local guiding paced around reserves and conservation.',
    },
    signals: {
      pt: ['Vida selvagem', 'Lodge direction', 'Guia local'],
      en: ['Wildlife', 'Lodge direction', 'Local guide'],
    },
  },
  {
    key: 'japan-golden-route',
    image:
      'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?auto=format&fit=crop&w=900&q=80',
    label: {
      pt: 'Rota curada',
      en: 'Curated route',
    },
    title: {
      pt: 'Rota clássica do Japão',
      en: 'Japan Golden Route',
    },
    description: {
      pt: 'Tóquio, Kyoto e Osaka com cultura, hotéis bem posicionados e logística ferroviária bem desenhada.',
      en: 'Tokyo, Kyoto and Osaka with culture, well-positioned hotels and thoughtful rail logistics.',
    },
    signals: {
      pt: ['Tóquio', 'Kyoto', 'Logística de trem'],
      en: ['Tokyo', 'Kyoto', 'Rail logistics'],
    },
  },
  {
    key: 'paris-champagne-loire',
    image:
      'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=900&q=80',
    label: {
      pt: 'Gastronomia e rota',
      en: 'Food & routing',
    },
    title: {
      pt: 'Paris, Champagne e Vale do Loire',
      en: 'Paris, Champagne & Loire',
    },
    description: {
      pt: 'Cidade, gastronomia, região de vinhos e uma rota refinada sem excesso de deslocamentos.',
      en: 'City, gastronomy, wine country and refined routing without unnecessary movement.',
    },
    signals: {
      pt: ['Paris', 'Champagne', 'Vale do Loire'],
      en: ['Paris', 'Champagne', 'Loire Valley'],
    },
  },
  {
    key: 'lencois-maranhenses',
    image:
      'https://images.unsplash.com/photo-1672271688662-3a03bbb75ec9?auto=format&fit=crop&w=900&q=80',
    label: {
      pt: 'Rota de aventura',
      en: 'Adventure route',
    },
    title: {
      pt: 'Aventura nos Lençóis Maranhenses',
      en: 'Lençóis Maranhenses Adventure',
    },
    description: {
      pt: 'Lagoas, travessias, bases certas e logística local para uma viagem de natureza com bom ritmo.',
      en: 'Lagoons, crossings, smart bases and local logistics for a nature-led trip with better pacing.',
    },
    signals: {
      pt: ['Lagoas', 'Travessias', 'Logística local'],
      en: ['Lagoons', 'Crossings', 'Local logistics'],
    },
  },
  {
    key: 'maldives-island-escape',
    image:
      'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=900&q=80',
    label: {
      pt: 'Refúgio romântico',
      en: 'Romantic retreat',
    },
    title: {
      pt: 'Refúgio nas Maldivas',
      en: 'Maldives Island Escape',
    },
    description: {
      pt: 'Ritmo de resort, reef, traslados e dias de descanso desenhados para evitar uma viagem pesada.',
      en: 'Resort rhythm, reef access, transfers and rest days shaped to keep the trip effortless.',
    },
    signals: {
      pt: ['Reef', 'Traslados', 'Ritmo de resort'],
      en: ['Reef', 'Transfers', 'Resort rhythm'],
    },
  },
  {
    key: 'victoria-falls-chobe',
    image:
      'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=900&q=80',
    label: {
      pt: 'Experiência assinatura',
      en: 'Signature experience',
    },
    title: {
      pt: 'Victoria Falls com extensão a Chobe',
      en: 'Victoria Falls & Chobe Extension',
    },
    description: {
      pt: 'Cataratas, safari, rio e logística entre países com tempo suficiente para a viagem respirar.',
      en: 'Waterfalls, safari, river time and cross-border logistics with enough room for the trip to breathe.',
    },
    signals: {
      pt: ['Cataratas', 'Safari', 'Logística regional'],
      en: ['Waterfalls', 'Safari', 'Regional logistics'],
    },
  },
];

export default function PremiumExperiences() {
  const { i18n } = useTranslation();
  const language = useMemo(() => getLanguage(i18n.resolvedLanguage || i18n.language), [i18n.language, i18n.resolvedLanguage]);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
          }
        });
      },
      { threshold: 0.1 }
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section
        id="experiencias"
        className="relative overflow-hidden bg-gradient-to-b from-[#0F1F38] to-[#08111F] px-5 py-20 sm:px-6 lg:py-28"
      >
        <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-[#C9A84C]/10 blur-3xl" />

        <div className="mx-auto max-w-7xl">
          <div className="mb-12 lg:mb-16">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-4 py-2">
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E8D4A0]">
                {sectionCopy.badge[language]}
              </span>
            </div>

            <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <h2 className="mb-4 text-4xl font-extrabold leading-tight text-white sm:text-5xl">
                  {sectionCopy.title[language]}
                </h2>
                <p className="max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg">
                  {sectionCopy.description[language]}
                </p>
              </div>

              <div className="inline-flex shrink-0 items-center gap-3 rounded-2xl border border-[#C9A84C]/35 bg-[#C9A84C]/10 px-5 py-4 text-sm font-semibold text-[#E8D4A0]">
                <i className="ri-compass-3-line text-lg" aria-hidden="true" />
                <span>{sectionCopy.catalogNote[language]}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3" data-travel-ideas>
            {travelIdeas.map((idea, index) => (
              <article
                key={idea.key}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                className="group relative min-h-[500px] w-full overflow-hidden rounded-3xl border border-white/10 bg-[#0A1628] text-left opacity-0 shadow-2xl shadow-black/20 transition-all duration-700 translate-y-10"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <img
                  src={idea.image}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-center opacity-95 transition-transform duration-700 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#07111F]/95 via-[#07111F]/45 to-[#07111F]/5" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/10 via-transparent to-[#0A1628]/25" />

                <div className="absolute inset-x-0 bottom-0 z-10 p-6">
                  <span className="mb-3 inline-flex rounded-full border border-[#C9A84C]/70 bg-[#07111F]/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#E8D4A0] backdrop-blur">
                    {idea.label[language]}
                  </span>

                  <h3 className="mb-3 text-2xl font-extrabold leading-tight text-white">
                    {idea.title[language]}
                  </h3>

                  <p className="mb-5 text-sm leading-relaxed text-white/90">
                    {idea.description[language]}
                  </p>

                  <div className="mb-5 flex flex-wrap gap-2">
                    {idea.signals[language].map((signal) => (
                      <span
                        key={signal}
                        className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white/80"
                      >
                        {signal}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-end justify-between gap-4 border-t border-white/10 pt-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E8D4A0]">
                        Travel Design
                      </p>
                      <p className="mt-1 text-sm font-semibold text-white/90">
                        {sectionCopy.investment[language]}
                      </p>
                    </div>

                    <span className="inline-flex items-center gap-2 rounded-full border border-[#C9A84C]/35 bg-[#C9A84C]/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#E8D4A0]">
                      <i className="ri-sparkling-2-line" aria-hidden="true" />
                      {sectionCopy.designLabel[language]}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-[#08111F] to-[#0D182B] px-5 py-16 sm:px-6 lg:py-20">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="mb-6 text-3xl font-semibold text-white">
            {sectionCopy.processTitle[language]}
          </h2>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {sectionCopy.process.map((step) => (
              <div key={step.title.en} className="rounded-2xl border border-white/10 bg-white/5 p-7 shadow-sm">
                <h3 className="mb-2 font-semibold text-white">{step.title[language]}</h3>
                <p className="text-sm leading-relaxed text-white/80">{step.description[language]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
